/**
 * client/src/lib/use-notifications.ts
 *
 * Reads the aggregated notification feed (`GET /api/notifications`).
 *
 * Replaces the bell's previous `useAlerts` source, which read ONLY the
 * warehouse-backed anomaly-events table — so the bell rendered "No active
 * alerts" even for an org with open approvals, pending challenges, and
 * unaccepted invites.
 *
 * `orgId` may be null (personal context): the feed still returns the caller's
 * own account-level alerts.
 */

import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { NotificationFeed, NotificationItem } from "@shared/notification-types";

/** Poll interval — the feed is a live read over the operational DB. */
const REFETCH_MS = 60_000;

/** Extends the shared wire contract so client and server cannot drift. */
export interface NotificationFeedResponse extends NotificationFeed {
  readonly items: NotificationItem[];
  readonly degradedSources: string[];
}

export interface UseNotificationsResult extends NotificationFeedResponse {
  readonly isLoading: boolean;
}

/** The react-query key prefix, exported so mutations can invalidate the feed. */
export const NOTIFICATIONS_QUERY_KEY = "notifications";

export interface UseNotificationsOptions {
  /**
   * Set false to hold the query — used while an org context's id is still
   * resolving, so the bell shows a loading state rather than a confident empty
   * one built from the wrong (personal) scope.
   */
  readonly enabled?: boolean;
}

export function useNotifications(
  orgId: string | null,
  options: UseNotificationsOptions = {},
): UseNotificationsResult {
  const enabled = options.enabled ?? true;
  const query = orgId ? `?orgId=${encodeURIComponent(orgId)}` : "";

  const { data, isLoading, isError } = useQuery<NotificationFeedResponse>({
    queryKey: [NOTIFICATIONS_QUERY_KEY, orgId],
    queryFn: () => apiRequest(`/api/notifications${query}`),
    refetchInterval: REFETCH_MS,
    enabled,
  });

  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    actionable: data?.actionable ?? 0,
    /**
     * A FAILED REQUEST IS DEGRADED (load-bearing — this line is the last hop of
     * the whole honesty chain). On a 500, a 403, or a network failure,
     * react-query settles to `isError` with `data` undefined, which would
     * otherwise fall through the `?? false` below and render the clean
     * "nothing needs your attention" empty state over a total outage — exactly
     * the false assurance the server-side `degraded` flag exists to prevent
     * (CLAUDE.md §Friction Rule clause 2). Whichever signal fires, the drawer
     * must say the list may be incomplete.
     */
    degraded: isError || (data?.degraded ?? false),
    degradedSources: data?.degradedSources ?? [],
    isLoading,
  };
}
